import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, getApiErrorMessage } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { PIPELINE_STAGES, currency, fmtDate, fmtDateTime, countryFlag, STAGE_BY_ID } from "@/lib/format";
import { Plane, FileText, Download } from "lucide-react";
import { toast } from "sonner";

export default function PatientPortal() {
  const { user } = useAuth();
  const [patient, setPatient] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data: list } = await api.get("/patients");
        if (list.length === 0) return;
        const p = list[0];
        const [tl, dc] = await Promise.all([
          api.get(`/patients/${p.id}/timeline`),
          api.get(`/documents?patient_id=${p.id}`),
        ]);
        setPatient(p); setTimeline(tl.data); setDocs(dc.data);
      } catch (e) { toast.error(getApiErrorMessage(e)); }
    })();
  }, []);

  if (!patient) {
    return (
      <div>
        <PageHeader eyebrow="My Journey" title={`Hello, ${user?.name}`} subtitle="Your treatment journey will appear here once a coordinator sets up your profile." />
        <div className="bg-white border border-[#E8E6E1] rounded-2xl p-10 text-center">
          <Plane size={28} className="text-[#2A4D3E] mx-auto mb-3" strokeWidth={1.6} />
          <div className="font-heading text-lg text-[#2A332C]">No patient profile linked yet</div>
          <div className="text-sm text-[#5C6660] mt-2">Your coordinator will share access soon. Reach out if you need help.</div>
        </div>
      </div>
    );
  }

  const stageIdx = PIPELINE_STAGES.findIndex((s) => s.id === patient.stage);

  return (
    <div data-testid="patient-portal">
      <PageHeader
        eyebrow={`${countryFlag(patient.country)} ${patient.country || ""}`}
        title={`Welcome, ${patient.name}`}
        subtitle={`Your ${patient.treatment.toLowerCase()} journey at a glance.`}
      />

      <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#8E9992] font-semibold mb-1">Current stage</div>
            <StatusBadge kind="stage" value={patient.stage} />
          </div>
          <div className="text-xs text-[#8E9992]">Step {stageIdx + 1} of {PIPELINE_STAGES.length}</div>
        </div>
        <div className="h-2 bg-[#F2F4F3] rounded-full overflow-hidden">
          <div className="h-full bg-[#2A4D3E] transition-all" style={{ width: `${((stageIdx + 1) / PIPELINE_STAGES.length) * 100}%` }} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-[#E8E6E1]">
          <KV label="Treatment" value={patient.treatment} />
          <KV label="Arrival" value={fmtDate(patient.arrival_date) || "—"} />
          <KV label="Discharge" value={fmtDate(patient.discharge_date) || "—"} />
          <KV label="Estimated value" value={currency(patient.treatment_value)} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6">
          <div className="font-heading font-semibold text-lg mb-5 text-[#2A332C]">Your journey</div>
          <div className="relative border-l-2 border-[#E8E6E1] ml-2 space-y-5">
            {timeline.map((t) => (
              <div key={t.id} className="relative pl-6">
                <div className="absolute w-3 h-3 rounded-full bg-[#2A4D3E] -left-[7px] top-1 border-2 border-white" />
                <div className="text-sm font-medium text-[#2A332C]">{t.title}</div>
                {t.description && <div className="text-xs text-[#5C6660] mt-0.5">{t.description}</div>}
                <div className="text-[10px] text-[#8E9992] mt-1">{fmtDateTime(t.created_at)}</div>
              </div>
            ))}
            {timeline.length === 0 && <div className="text-sm text-[#8E9992] pl-4">No updates yet.</div>}
          </div>
        </div>

        <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6">
          <div className="font-heading font-semibold text-lg mb-5 text-[#2A332C]">Your documents</div>
          <div className="space-y-2">
            {docs.map((d) => (
              <div key={d.id} className="flex items-center justify-between p-3 border border-[#E8E6E1] rounded-xl">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-[#E6F0EB] flex items-center justify-center"><FileText size={14} className="text-[#2A4D3E]" /></div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#2A332C] truncate">{d.name}</div>
                    <div className="text-[11px] text-[#8E9992] capitalize">{d.category?.replace("_", " ")}</div>
                  </div>
                </div>
                <button onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}`, "_blank")}
                  className="text-xs text-[#2A4D3E] font-semibold hover:underline flex items-center gap-1">
                  <Download size={12} /> View
                </button>
              </div>
            ))}
            {docs.length === 0 && <div className="text-sm text-[#8E9992]">No documents shared yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#8E9992] font-semibold">{label}</div>
      <div className="text-sm font-medium text-[#2A332C]">{value}</div>
    </div>
  );
}
